import { useState, useMemo, useRef, useCallback } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

import { ArtistSearch } from './components/ArtistSearch';
import { ServiceSelector, ServiceSelection } from './components/ServiceSelector';
import { DealInputs } from './components/DealInputs';
import { ArtistSnapshotCard } from './components/ArtistSnapshotCard';
import { TrajectoryChart } from './components/TrajectoryChart';
import { RevenueProjectionCards } from './components/RevenueProjectionCards';
import { DealComparisonTable } from './components/DealComparisonTable';
import { VerdictCard } from './components/VerdictCard';
import { BreakEvenDisplay } from './components/BreakEvenDisplay';
import { RiskScoreCard } from './components/RiskScoreCard';
import { SyncScoreCard } from './components/SyncScoreCard';
import { BrandScoreCard } from './components/BrandScoreCard';
import { CounterOfferCard } from './components/CounterOfferCard';

import { useArtistDealData } from './hooks/useArtistDealData';
import { SERVICE_RATES, ServiceKey } from './utils/constants';
import {
    calculateMonthlyGrowthRate,
    projectListeners,
    calculateTwoPhaseRevenue,
    analyzeDeal,
    buildDealComparison,
    SelectedService,
} from './utils/dealCalculations';
import { estimateLiveRevenue } from './utils/revenueProjections';
import {
    calculateRiskScore,
    calculateSyncScore,
    calculateBrandScore,
} from './utils/riskAssessment';

const initialServices: ServiceSelection[] = Object.entries(SERVICE_RATES).map(
    ([key, val]) => ({
        key: key as ServiceKey,
        enabled: key === 'fullProduction',
        rate: val.rate,
    })
);

export const DealCalculatorPage = () => {
    const [artistId, setArtistId] = useState<string | null>(null);
    const [services, setServices] = useState<ServiceSelection[]>(initialServices);
    const [upfront, setUpfront] = useState(0);
    const [split, setSplit] = useState(10);
    const [postRecoupSplit, setPostRecoupSplit] = useState(50);
    const [tracksProduced, setTracksProduced] = useState(1);
    const [releaseBoost, setReleaseBoost] = useState(3);
    const [calculated, setCalculated] = useState(false);

    const dealData = useArtistDealData(artistId);

    const selectedServices: SelectedService[] = services
        .filter((s) => s.enabled)
        .map((s) => ({ key: s.key, rate: s.rate }));

    // Calculations (memoized)
    const analysis = useMemo(() => {
        if (!calculated || !dealData.metrics) return null;

        const growthRate = calculateMonthlyGrowthRate(dealData.history);
        const currentListeners = dealData.metrics.spotify_monthly_listeners || 0;
        const projections = projectListeners(currentListeners, growthRate, 36);

        const risk = calculateRiskScore(
            dealData.metrics,
            growthRate,
            dealData.history.length
        );

        const deal = analyzeDeal(
            selectedServices,
            upfront,
            split,
            projections,
            dealData.catalogSize,
            tracksProduced,
            risk.score,
            releaseBoost,
            postRecoupSplit
        );
        const streamingRevenue = calculateTwoPhaseRevenue(
            projections,
            split,
            postRecoupSplit,
            upfront,
            deal.standardFee,
            dealData.catalogSize,
            tracksProduced,
            releaseBoost
        );
        const liveRevenue = estimateLiveRevenue(dealData.events, split);
        const dealComparison = buildDealComparison(
            selectedServices,
            upfront,
            split,
            deal.fairSplitPercent,
            projections,
            dealData.catalogSize,
            tracksProduced,
            releaseBoost,
            postRecoupSplit
        );
        const sync = calculateSyncScore(dealData.metrics);
        const brand = calculateBrandScore(dealData.metrics);

        // Counter offer table: what split is needed at different upfront amounts
        const fee = deal.standardFee;
        const upfrontOptions = [0, 500, 1000, 1500, 2000, 2500, 3000, fee];
        if (!upfrontOptions.includes(upfront)) {
            upfrontOptions.push(upfront);
        }
        const uniqueUpfronts = [...new Set(upfrontOptions)].sort((a, b) => a - b);

        // Backend at 100% recoup split (max possible), with post-recoup drop
        const maxBackend = calculateTwoPhaseRevenue(
            projections, 100, postRecoupSplit, 0, fee,
            dealData.catalogSize, tracksProduced, releaseBoost
        ).find((r) => r.period === 24)?.moderate ?? 0;

        const counterRows = uniqueUpfronts.map((up) => {
            const gap = Math.max(0, fee - up);
            if (gap <= 0) {
                return { upfront: up, splitNeeded: 0, backend24: 0, total: up };
            }
            if (maxBackend <= 0 || gap > maxBackend) {
                return { upfront: up, splitNeeded: -1, backend24: maxBackend, total: up + maxBackend };
            }
            // Find the recoup-phase split % where two-phase backend24 >= gap
            const splitNeeded = Math.ceil((gap / maxBackend) * 100);
            const actualBackend = calculateTwoPhaseRevenue(
                projections, splitNeeded, postRecoupSplit, up, fee,
                dealData.catalogSize, tracksProduced, releaseBoost
            ).find((r) => r.period === 24)?.moderate ?? 0;
            return { upfront: up, splitNeeded, backend24: actualBackend, total: up + actualBackend };
        });

        return {
            growthRate,
            currentListeners,
            projections,
            streamingRevenue,
            liveRevenue,
            deal,
            dealComparison,
            risk,
            sync,
            brand,
            counterRows,
        };
    }, [calculated, dealData, split, postRecoupSplit, upfront, tracksProduced, releaseBoost, selectedServices]);

    const handleCalculate = () => {
        if (!dealData.metrics) return;
        setCalculated(true);
    };

    const handleArtistSelect = (id: string | null, _imageUrl?: string | null) => {
        setArtistId(id);
        setCalculated(false);
    };

    const resultsRef = useRef<HTMLDivElement>(null);
    const [exporting, setExporting] = useState(false);

    const handleDownloadPdf = useCallback(async () => {
        if (!resultsRef.current || !dealData.metrics) return;
        setExporting(true);
        try {
            const artistName = dealData.artistName || 'Unknown';
            const dateStr = new Date().toISOString().slice(0, 10);

            const canvas = await html2canvas(resultsRef.current, {
                scale: 1.5,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
            });

            const imgData = canvas.toDataURL('image/jpeg', 0.85);
            const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const headerHeight = 12;

            // Header
            pdf.setFontSize(14);
            pdf.text(`Deal Analysis — ${artistName} — ${dateStr}`, margin, margin + 6);

            const contentWidth = pageWidth - margin * 2;
            const imgAspect = canvas.height / canvas.width;
            const imgWidth = contentWidth;
            const imgHeight = imgWidth * imgAspect;

            const startY = margin + headerHeight;
            const availableHeight = pageHeight - startY - margin;

            if (imgHeight <= availableHeight) {
                pdf.addImage(imgData, 'JPEG', margin, startY, imgWidth, imgHeight);
            } else {
                // Split across pages
                let srcY = 0;
                const srcWidth = canvas.width;
                const sliceCanvasHeight = (availableHeight / imgWidth) * srcWidth;
                let isFirstPage = true;

                while (srcY < canvas.height) {
                    if (!isFirstPage) {
                        pdf.addPage();
                    }
                    const yOffset = isFirstPage ? startY : margin;
                    const sliceHeight = isFirstPage
                        ? sliceCanvasHeight
                        : ((pageHeight - margin * 2) / imgWidth) * srcWidth;
                    const remaining = canvas.height - srcY;
                    const currentSlice = Math.min(sliceHeight, remaining);

                    const sliceCanvas = document.createElement('canvas');
                    sliceCanvas.width = srcWidth;
                    sliceCanvas.height = currentSlice;
                    const ctx = sliceCanvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(
                            canvas,
                            0, srcY, srcWidth, currentSlice,
                            0, 0, srcWidth, currentSlice
                        );
                    }

                    const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.85);
                    const drawHeight = (currentSlice / srcWidth) * imgWidth;
                    pdf.addImage(sliceData, 'JPEG', margin, yOffset, imgWidth, drawHeight);

                    srcY += currentSlice;
                    isFirstPage = false;
                }
            }

            const safeName = artistName.replace(/[^a-zA-Z0-9]/g, '_');
            pdf.save(`Deal_Analysis_${safeName}_${dateStr}.pdf`);
        } catch (err) {
            console.error('PDF export failed:', err);
        } finally {
            setExporting(false);
        }
    }, [dealData.metrics, dealData.artistName]);

    return (
        <Stack spacing={3} sx={{ p: 2 }}>
            <Typography variant="h4">Deal Calculator</Typography>

            {/* Input Section */}
            <Card>
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <ArtistSearch onSelect={handleArtistSelect} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <ServiceSelector
                                services={services}
                                onChange={setServices}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <DealInputs
                                upfront={upfront}
                                split={split}
                                postRecoupSplit={postRecoupSplit}
                                onUpfrontChange={setUpfront}
                                onSplitChange={setSplit}
                                onPostRecoupSplitChange={setPostRecoupSplit}
                            />
                            <Box mt={2} display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
                                <TextField
                                    label="Tracks Produced"
                                    type="number"
                                    size="small"
                                    value={tracksProduced}
                                    onChange={(e) => setTracksProduced(Math.max(1, parseInt(e.target.value) || 1))}
                                    inputProps={{ min: 1, max: 20 }}
                                    sx={{ width: 130 }}
                                />
                                <TextField
                                    label="Release Boost"
                                    type="number"
                                    size="small"
                                    value={releaseBoost}
                                    onChange={(e) => setReleaseBoost(Math.max(1, parseFloat(e.target.value) || 1))}
                                    inputProps={{ min: 1, max: 10, step: 0.5 }}
                                    helperText="2x normal, 5x pushed single"
                                    sx={{ width: 150 }}
                                />
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                                    {dealData.catalogSize} tracks in catalog
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleCalculate}
                            disabled={
                                !dealData.metrics ||
                                dealData.loading ||
                                selectedServices.length === 0
                            }
                        >
                            Calculate Deal
                        </Button>
                        {analysis && (
                            <Button
                                variant="outlined"
                                size="large"
                                onClick={handleDownloadPdf}
                                disabled={exporting}
                                startIcon={exporting ? <CircularProgress size={18} /> : undefined}
                            >
                                {exporting ? 'Exporting…' : 'Download PDF'}
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {/* Loading / Error states */}
            {dealData.loading && (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            )}

            {dealData.error && (
                <Alert severity="error">{dealData.error}</Alert>
            )}

            {/* Artist Snapshot */}
            {dealData.metrics && (
                <ArtistSnapshotCard
                    metrics={dealData.metrics}
                    events={dealData.events}
                    editorialPlaylistCount={dealData.editorialPlaylistCount}
                />
            )}

            {/* Warning for sparse data */}
            {dealData.metrics && dealData.history.length < 2 && (
                <Alert severity="warning">
                    Limited historical data ({dealData.history.length} snapshot
                    {dealData.history.length !== 1 ? 's' : ''}). Growth
                    projections will assume 0% growth. No data = no discount =
                    pay the full rate.
                </Alert>
            )}

            {/* Calculated results */}
            {analysis && (
                <Box ref={resultsRef} sx={{ backgroundColor: '#ffffff' }}>
                <Stack spacing={3}>
                    <VerdictCard
                        verdict={analysis.deal.verdict}
                        reason={analysis.deal.verdictReason}
                        fairSplit={analysis.deal.fairSplitPercent}
                    />

                    <CounterOfferCard
                        standardFee={analysis.deal.standardFee}
                        counterRows={analysis.counterRows}
                        proposedUpfront={upfront}
                        proposedSplit={split}
                    />

                    <TrajectoryChart
                        history={dealData.history}
                        projections={analysis.projections}
                        currentListeners={analysis.currentListeners}
                    />

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <RevenueProjectionCards
                                streamingRevenue={analysis.streamingRevenue}
                                liveRevenueAnnual={analysis.liveRevenue.producerShare}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DealComparisonTable rows={analysis.dealComparison} />
                        </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <BreakEvenDisplay
                                recoupMonths={analysis.deal.recoupMonths}
                                standardTotal={analysis.deal.standardFee}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <RiskScoreCard risk={analysis.risk} />
                        </Grid>
                    </Grid>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <SyncScoreCard sync={analysis.sync} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <BrandScoreCard brand={analysis.brand} />
                        </Grid>
                    </Grid>
                </Stack>
                </Box>
            )}
        </Stack>
    );
};
